import React from "react";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

const sections = [
  {
    label: "Back Office Services",
    features: [
      { name: "Insurance Verification", them: true },
      { name: "Insurance Claim Submission", them: true },
      { name: "Patient Billing & Statements", them: true },
      { name: "Payment Reconciliation", them: true },
      { name: "EOB Posting", them: true },
    ],
  },
  {
    label: "Only with Asha",
    features: [
      { name: "Denial Management", them: false },
      { name: "Revenue Analytics", them: false },
      { name: "Integrated Payments & Billing Suite", them: false },
      { name: "AI Insurance Caller", them: false },
    ],
  },
];

const totalTheirs = sections.reduce((sum, s) => sum + s.features.filter(f => f.them).length, 0);
const totalFeatures = sections.reduce((sum, s) => sum + s.features.length, 0);

const PricingComparisonV3Slide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex items-stretch p-5 xl:p-8 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs — blue scheme */}
      <div className="absolute top-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }} />
      <div className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }} />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex gap-10 relative z-10 h-full max-w-[1240px] mx-auto items-center">
        {/* Left side — title */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-[420px] flex-shrink-0 flex flex-col justify-center"
        >
          <h2 className="text-[40px] lg:text-[46px] font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-8">
            Back Office{" "}
            <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
              Pricing Comparison
            </span>
          </h2>
          <div className="space-y-5">
            <p className="text-[16px] text-slate-600 leading-relaxed font-medium">
              eAssist and Wisdom charge{" "}
              <span className="text-slate-900 font-bold">
                $3,600/mo + 3.5% of collections
              </span>{" "}
              — and you still don't get denial management, revenue analytics, integrated payments, or an AI insurance caller.
            </p>
            <div className="flex gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="w-8 h-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center shadow-sm shadow-blue-500/20">
                  <Check className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <p className="text-[16px] text-slate-600 leading-relaxed font-medium">
                Asha delivers everything they offer — plus the services they don't — for{" "}
                <span className="text-blue-600 font-bold">
                  just 3.5% of collections.
                </span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Right side — comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex-1 rounded-[24px] overflow-hidden shadow-[0_20px_80px_-20px_rgba(0,0,0,0.12)] border border-slate-200 bg-white flex flex-col h-[640px] relative"
        >
          {/* Column headers (Sticky) */}
          <div className="grid grid-cols-[1fr_260px_260px] border-b border-slate-200 flex-shrink-0 bg-white z-20 relative shadow-sm">
            <div className="px-6 py-5 flex items-end">
              <span className="text-xs font-black uppercase tracking-widest text-slate-400">Features Included</span>
            </div>
            <div className="px-4 py-5 text-center border-l border-slate-100 bg-gradient-to-b from-slate-50 to-white relative flex flex-col justify-end">
              <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">eAssist / Wisdom</p>
              <span className="text-[13.5px] font-extrabold text-slate-800 whitespace-nowrap tracking-tight">$3,600/mo + 3.5% of collections</span>
            </div>
            <div className="px-4 py-5 text-center border-l border-blue-100 bg-gradient-to-b from-blue-50/80 to-white relative flex flex-col justify-end items-center">
              {/* Highlight badge */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-widest mb-2 w-fit">
                Asha OS
              </div>
              <p className="text-xs font-black uppercase tracking-wider text-blue-600 mb-2">Asha</p>
              <span className="text-[13.5px] font-extrabold text-slate-900 whitespace-nowrap tracking-tight">Just 3.5% of collections</span>
            </div>
          </div>

          {/* Feature rows */}
          <div className="flex-1 bg-white overflow-hidden flex flex-col">
            {sections.map((section, si) => (
              <React.Fragment key={si}>
                <div className="grid grid-cols-[1fr_260px_260px] bg-slate-50/90 border-y border-slate-100">
                  <div className="px-6 py-2 flex items-center gap-2">
                    <div className="w-1 h-3.5 rounded-full bg-blue-400/50"></div>
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-600">{section.label}</span>
                  </div>
                  <div className="border-l border-slate-100" />
                  <div className="border-l border-blue-50/50 bg-blue-50/20" />
                </div>
                {section.features.map((f, fi) => (
                  <div key={fi} className="grid grid-cols-[1fr_260px_260px] border-b border-slate-50/50 hover:bg-slate-50/50 transition-colors group flex-1 min-h-0">
                    <div className="px-6 py-1.5 text-[13.5px] text-slate-700 font-medium flex items-center group-hover:text-slate-900 transition-colors">
                      {f.name}
                    </div>
                    <div className="py-1.5 flex items-center justify-center border-l border-slate-100/50">
                      {f.them ? (
                        <div className="w-[24px] h-[24px] rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
                          <Check className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                      ) : (
                        <div className="w-[24px] h-[24px] rounded-full flex items-center justify-center opacity-30">
                          <X className="w-3.5 h-3.5 text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div className="py-1.5 flex items-center justify-center border-l border-blue-50/50 bg-blue-50/20 group-hover:bg-blue-50/40 transition-colors">
                      <div className="w-[24px] h-[24px] rounded-full bg-blue-100 flex items-center justify-center border border-blue-200 shadow-[0_2px_8px_rgba(59,130,246,0.15)]">
                        <Check className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>

          {/* Summary bar */}
          <div className="grid grid-cols-[1fr_260px_260px] border-t border-slate-200 bg-white flex-shrink-0 z-20 relative shadow-[0_-10px_20px_rgba(0,0,0,0.03)]">
            <div className="px-6 py-3.5 flex items-center">
              <span className="text-xs font-black text-slate-400 uppercase tracking-wider">What you get</span>
            </div>
            <div className="py-3.5 text-center border-l border-slate-100 bg-slate-50">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-[19px] font-black text-slate-700">{totalTheirs}</span>
                <span className="text-xs font-bold text-slate-400">/ {totalFeatures}</span>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mt-0.5">Capabilities</span>
            </div>
            <div className="py-3.5 text-center border-l border-blue-100 bg-blue-50">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-[19px] font-black text-blue-700">{totalFeatures}</span>
                <span className="text-xs font-bold text-blue-400">/ {totalFeatures}</span>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-500 block mt-0.5">Capabilities</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingComparisonV3Slide;
